import { useState, useEffect, useCallback } from 'react';
import { getKidsChildren, upsertKidsChild } from '../../utils/api';
import { GroupPill, AllergyText, usePager, formatPhone, cleanAllergy } from './kidsUi';

// Master allergy list — every kid with an allergy, searchable by name and
// sortable/filterable by program and group, with totals by type up top.
// Everyone can SEE this tab; only staff/2ICs can edit (override) an allergy.
// Nametags still print from PCO; parents flag errors day-of.

const PROGRAMS = ['Elementary', '5-6', '3-4'];
const TYPE_CHIPS = ['Gluten-Free', 'Dairy-Free', 'Dye-Free', 'Nut Allergy', 'Egg Allergy'];

export default function KidsAllergies({ team, showToast, readOnly }) {
  const [children, setChildren] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [editing, setEditing] = useState(null); // child being overridden
  const [overrideVal, setOverrideVal] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    getKidsChildren()
      .then(d => { setChildren(d?.children || []); setGroups(d?.groups || []); })
      .catch(() => showToast('Could not load allergies — check the Apps Script deploy.'))
      .finally(() => setLoading(false));
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const groupById = {};
  groups.forEach(g => { groupById[g.id] = g; });

  const active = children.filter(c => c.status !== 'Cancelled');
  // cleanAllergy drops "na"/"N/A"/"none" — those mean no allergy.
  const withAllergies = active.filter(c => cleanAllergy(c.displayAllergies) !== '');

  const counts = {};
  TYPE_CHIPS.forEach(t => { counts[t] = 0; });
  let otherCount = 0;
  withAllergies.forEach(c => {
    const tags = (c.allergyOverride || c.allergyTags || '').toString();
    let matched = false;
    TYPE_CHIPS.forEach(t => { if (tags.includes(t)) { counts[t]++; matched = true; } });
    if (!matched) otherCount++;
  });

  const filtered = withAllergies.filter(c => {
    if (programFilter && c.program !== programFilter) return false;
    if (groupFilter && c.groupId !== groupFilter) return false;
    if (typeFilter) {
      const tags = (c.allergyOverride || c.allergyTags || '').toString();
      if (typeFilter === '__other') {
        if (TYPE_CHIPS.some(t => tags.includes(t))) return false;
      } else if (!tags.includes(typeFilter)) return false;
    }
    if (query) {
      const q = query.toLowerCase();
      if (!`${c.firstName || ''} ${c.lastName || ''}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Sort by program, then group, then name — the "sorted by age group and
  // group" view the meeting asked for.
  filtered.sort((a, b) => {
    const pa = PROGRAMS.indexOf(a.program); const pb = PROGRAMS.indexOf(b.program);
    if (pa !== pb) return (pa === -1 ? 99 : pa) - (pb === -1 ? 99 : pb);
    const ga = (groupById[a.groupId]?.name || 'zzz'); const gb = (groupById[b.groupId]?.name || 'zzz');
    if (ga !== gb) return ga.localeCompare(gb);
    return `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`);
  });

  const { slice, controls } = usePager(filtered, 25);

  async function saveOverride() {
    if (!editing) return;
    const child = editing;
    const val = String(overrideVal ?? '').trim(); // sheet values can be numeric — (5).trim() throws
    setEditing(null);
    try {
      await upsertKidsChild({ id: child.id, allergyOverride: val });
      setChildren(list => list.map(c => c.id === child.id ? { ...c, allergyOverride: val, displayAllergies: val || (c.allergyTags || c.allergyRaw || '') } : c));
      showToast(val ? 'Allergy updated.' : 'Override cleared — back to the registration value.');
    } catch (e) {
      showToast(`Save failed — ${e.message || 'try again'}`);
    }
  }

  return (
    <>
      <div style={{ padding: '24px 40px 16px', background: 'var(--white)', borderBottom: '1px solid var(--border)' }}>
        <div className="page-title-row" style={{ marginBottom: 16 }}>
          <div>
            <div className="page-title">Allergies</div>
            <div className="page-sub">{withAllergies.length} kids with allergies · pulled from registration · {readOnly ? 'view only' : 'staff can override'}</div>
          </div>
        </div>

        {/* Totals by type — for planning quantities */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {TYPE_CHIPS.map(t => (
            <button key={t} className={`stat-chip${typeFilter === t ? ' active-chip' : ''}`} onClick={() => setTypeFilter(typeFilter === t ? '' : t)}>
              <span className="stat-chip-num">{counts[t]}</span> {t}
            </button>
          ))}
          <button className={`stat-chip${typeFilter === '__other' ? ' active-chip' : ''}`} onClick={() => setTypeFilter(typeFilter === '__other' ? '' : '__other')}>
            <span className="stat-chip-num">{otherCount}</span> Other
          </button>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginRight: 2 }}>Program</span>
          <button className={`stat-chip${!programFilter ? ' active-chip' : ''}`} onClick={() => { setProgramFilter(''); setGroupFilter(''); }}>All</button>
          {PROGRAMS.map(p => (
            <button key={p} className={`stat-chip${programFilter === p ? ' active-chip' : ''}`} onClick={() => { setProgramFilter(programFilter === p ? '' : p); setGroupFilter(''); }}>
              {p}
            </button>
          ))}
          {programFilter && groups.filter(g => g.program === programFilter).map(g => (
            <button key={g.id} className={`stat-chip${groupFilter === g.id ? ' active-chip' : ''}`} onClick={() => setGroupFilter(groupFilter === g.id ? '' : g.id)}>
              {g.name}
            </button>
          ))}
        </div>
      </div>

      <div className="page-content" style={{ paddingTop: 24 }}>
        <div style={{ marginBottom: 12, maxWidth: 360 }}>
          <input className="search-bar" placeholder="Search by kid's name…" value={query} onChange={e => setQuery(e.target.value)} />
        </div>

        <div className="panel">
          {loading ? (
            <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>
              No allergies match this filter.
            </div>
          ) : (
            <>
              <table className="data-table">
                <thead>
                  <tr><th>Name</th><th>Program</th><th>Group</th><th>Allergy</th><th>Parent</th>{!readOnly && <th></th>}</tr>
                </thead>
                <tbody>
                  {slice.map(c => (
                    <tr key={c.id} style={{ cursor: 'default' }}>
                      <td><span className="td-name">{c.firstName} {c.lastName}</span></td>
                      <td>{c.program || '—'}</td>
                      <td>{c.groupId && groupById[c.groupId] ? <GroupPill name={groupById[c.groupId].name} color={groupById[c.groupId].color} /> : <span style={{ color: 'var(--subtle)' }}>—</span>}</td>
                      <td>
                        <AllergyText>{cleanAllergy(c.displayAllergies)}</AllergyText>
                        {c.allergyOverride && <span className="pill pill-gray" style={{ fontSize: 9, marginLeft: 6 }}>Edited</span>}
                        {c.allergyOverride && cleanAllergy(c.allergyRaw) && cleanAllergy(c.allergyRaw).toLowerCase() !== cleanAllergy(c.allergyOverride).toLowerCase() && (
                          <div style={{ fontFamily: 'var(--font-b)', fontSize: 11, fontWeight: 300, color: 'var(--muted)', marginTop: 3 }}>
                            Registration says: {c.allergyRaw}
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>{c.parentName || '—'}{c.parentContact ? ` · ${formatPhone(c.parentContact)}` : ''}</td>
                      {!readOnly && (
                        <td>
                          <button className="btn btn-xs" onClick={() => { setEditing(c); setOverrideVal(c.allergyOverride || c.displayAllergies || ''); }}>Edit</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {controls}
            </>
          )}
        </div>
      </div>

      {editing && (
        <div className="modal-overlay open" onClick={e => { if (e.target === e.currentTarget) setEditing(null); }}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">Edit Allergy — {editing.firstName} {editing.lastName}</div>
              <button className="modal-close" onClick={() => setEditing(null)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.6 }}>
                Registration says: <strong style={{ color: 'var(--black)' }}>{editing.allergyRaw || editing.allergyTags || 'nothing'}</strong>.
                Your override replaces what leaders see. Clear the box to go back to the registration value.
              </div>
              <div className="form-group">
                <label className="form-label">Allergy (what leaders should see)</label>
                <input className="form-input" value={overrideVal} onChange={e => setOverrideVal(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && saveOverride()} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn btn-dark" onClick={saveOverride}>Save</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
